import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { StoredAvatar } from '../ui/StoredAvatar';
import { Badge, Loading } from '../ui/Page';
import { ChannelMark } from './ChannelIcons';
import { AssignmentControls } from './AssignmentControls';
import {
  LIFECYCLE_STAGES,
  addNote,
  attachTag,
  detachTag,
  getRecord,
  listTags,
  updateRecord,
} from '../crm/detail-api';
import { QualifyButton } from '../crm/QualifyButton';
import { changesBetween, formOf, isDirty, problemsWith, type ContactForm } from './contact-form';
import type { ConversationDetail } from './api';
import type { ChannelKind } from './api';

/**
 * Who this is, beside the conversation (context.md §45–§56). Phase 144.
 *
 * It used to be a fixed column that was always there, holding a read-only summary. Two problems
 * with that, and they pull in opposite directions: the thread had a third of its width taken by
 * facts an agent already knows, and the facts themselves could not be corrected — a customer whose
 * name arrived from WhatsApp as "Marina" stayed "Marina" forever, because editing meant leaving
 * the conversation for the CRM screen and losing the reply already typed.
 *
 * So it slides, and it edits. Closed by default, because the thread is what somebody came for.
 *
 * **Nothing here remounts the conversation.** The panel is a sibling of the thread, so opening and
 * closing it cannot cost the messages, the scroll position or the draft — which is the one thing
 * this change could plausibly break and the reason it is built this way rather than as a route.
 */

export function ContactDrawer({
  conversation,
  channelKind,
  open,
  onClose,
}: {
  conversation: ConversationDetail;
  channelKind: ChannelKind | null;
  open: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ContactForm | null>(null);
  const [base, setBase] = useState<ContactForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [note, setNote] = useState('');
  const [newTag, setNewTag] = useState('');

  const contactId = conversation.contactId;

  const record = useQuery({
    queryKey: ['crm-record', 'contacts', contactId],
    queryFn: () => getRecord('contacts', contactId!),
    // Only when there is one, and only when somebody is looking: a closed panel fetches nothing.
    enabled: Boolean(contactId) && open,
  });

  const tags = useQuery({ queryKey: ['crm-tags'], queryFn: listTags, enabled: open && editing });

  /*
   * The draft belongs to one contact (requirement 4).
   *
   * Switching conversations while a form is open must not carry the previous customer's typing
   * into this one's record — the failure that writes one person's data onto another. The editor is
   * closed and the draft dropped whenever the contact changes, and the query key carries the id so
   * a slow response for the old contact cannot land in the new one's panel.
   */
  useEffect(() => {
    setEditing(false);
    setForm(null);
    setBase(null);
    setError(null);
    setSaved(false);
    setConfirmingDiscard(false);
    setNote('');
  }, [contactId]);

  const dirty = form !== null && base !== null && isDirty(base, form);

  /* Escape closes it, unless there is unsaved work — then it asks first. */
  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;

      if (dirty) setConfirmingDiscard(true);
      else onClose();
    };

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [open, dirty, onClose]);

  const startEditing = (): void => {
    if (!record.data) return;

    const next = formOf(record.data);

    setBase(next);
    setForm(next);
    setEditing(true);
    setError(null);
    setSaved(false);
  };

  const stopEditing = (): void => {
    setEditing(false);
    setForm(null);
    setBase(null);
    setConfirmingDiscard(false);
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!form || !base || !contactId) return null;

      const changes = changesBetween(base, form);

      if (!changes) return null;

      return updateRecord('contacts', contactId, changes);
    },
    onSuccess: () => {
      setError(null);
      setSaved(true);
      stopEditing();

      /*
       * The panel, the header and the list, without a reload.
       *
       * The conversation carries its own copy of the name — it is what the list draws — so the
       * inbox has to be told as well as the record, or the header keeps showing the old one until
       * somebody navigates away.
       */
      void client.invalidateQueries({ queryKey: ['crm-record', 'contacts', contactId] });
      void client.invalidateQueries({ queryKey: ['conversation', conversation.id] });
      void client.invalidateQueries({ queryKey: ['inbox'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const addTheNote = useMutation({
    mutationFn: () => addNote('CONTACT', contactId!, note.trim()),
    onSuccess: () => {
      setNote('');
      setError(null);
      void client.invalidateQueries({ queryKey: ['crm-timeline', contactId] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const tagIt = useMutation({
    mutationFn: (name: string) => attachTag('CONTACT', contactId!, name),
    onSuccess: () => {
      setNewTag('');
      void client.invalidateQueries({ queryKey: ['conversation', conversation.id] });
      void client.invalidateQueries({ queryKey: ['crm-tags'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const untagIt = useMutation({
    mutationFn: (tagId: string) => detachTag('CONTACT', contactId!, tagId),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['conversation', conversation.id] }),
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const name = conversation.peerName ?? conversation.peerAddress;
  const problem = form ? problemsWith(form) : null;

  const tagOptions = useMemo(
    () => (tags.data?.tags ?? []).filter((tag) => !conversation.tags.includes(tag.name)),
    [tags.data, conversation.tags],
  );

  const requestClose = (): void => {
    if (dirty) setConfirmingDiscard(true);
    else onClose();
  };

  const set = (field: keyof ContactForm, value: string | null): void => {
    setForm((current) => (current ? { ...current, [field]: value } : current));
    setSaved(false);
  };

  return (
    <aside
      className={`drawer-panel${open ? ' is-open' : ''}`}
      aria-label={t('inbox.contactDetails')}
      aria-hidden={!open}
      // Nothing inside a closed panel is reachable by keyboard: a tab stop in something nobody
      // can see is how a form gets filled in by accident.
      inert={!open}
    >
      <header className="drawer-panel__head">
        <h3>{t('inbox.contactDetails')}</h3>

        <button
          type="button"
          className="button button--quiet button--compact"
          onClick={requestClose}
          aria-label={t('common.close')}
        >
          ×
        </button>
      </header>

      <div className="drawer-panel__body">
        {/* 1. Identification. The face first: with a LID for an identifier it is the only thing
            an agent recognises at a glance. */}
        <section className="drawer-panel__block">
          <div className="contact-head contact-head--stacked">
            <StoredAvatar
              name={name}
              fileId={conversation.peerAvatarFileId}
              url={conversation.peerAvatarUrl}
              size="lg"
            />
            <span className="cell-stack">
              <span className="contact-head__name">{name}</span>
              <span className="muted small">{conversation.peerAddress}</span>
            </span>
          </div>

          {contactId ? (
            <div className="cell-inline">
              {!editing && (
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  onClick={startEditing}
                  disabled={record.isLoading}
                >
                  {t('inbox.editContact')}
                </button>
              )}

              {/*
               * "Esse aqui pode comprar" (Phase 147).
               *
               * Raised from the conversation because that is where somebody decides it — the
               * sentence that turns a contact into a prospect is usually one the customer just
               * wrote.
               */}
              {!editing && <QualifyButton contactId={contactId} contactName={name} compact />}

              <Link to={`/crm/registro/contacts/${contactId}`} className="small">
                {t('inbox.openContact')}
              </Link>
            </div>
          ) : (
            <span className="muted small">{t('inbox.noContact')}</span>
          )}

          {saved && <p className="muted small">{t('inbox.contactSaved')}</p>}

          {error && (
            <p className="alert alert--compact" role="alert">
              {error}
            </p>
          )}
        </section>

        {/* 2. The record itself, read or edited in place. */}
        {contactId && (
          <section className="drawer-panel__block">
            <h4>{t('inbox.contactRecord')}</h4>

            {record.isLoading ? (
              <Loading />
            ) : editing && form ? (
              <form
                className="stack"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!problem) save.mutate();
                }}
              >
                <label className="stack__field">
                  <span>{t('crm.field.first_name')}</span>
                  <input
                    className="input"
                    value={form.firstName}
                    maxLength={120}
                    onChange={(event) => set('firstName', event.target.value)}
                  />
                </label>

                <label className="stack__field">
                  <span>{t('crm.field.last_name')}</span>
                  <input
                    className="input"
                    value={form.lastName}
                    maxLength={120}
                    onChange={(event) => set('lastName', event.target.value)}
                  />
                </label>

                <label className="stack__field">
                  <span>{t('crm.field.email')}</span>
                  <input
                    className="input"
                    type="email"
                    value={form.email}
                    maxLength={254}
                    onChange={(event) => set('email', event.target.value)}
                  />
                </label>

                <label className="stack__field">
                  <span>{t('crm.field.phone')}</span>
                  <input
                    className="input"
                    value={form.phone}
                    maxLength={40}
                    onChange={(event) => set('phone', event.target.value)}
                  />
                </label>

                {/*
                 * The number the channel knows them by is shown and not edited.
                 *
                 * It is what ties this person to the thread and to the provider; letting somebody
                 * retype it here would silently detach the conversation from the contact.
                 */}
                <div className="contact-field">
                  <span className="contact-field__label">{t('inbox.channelNumber')}</span>
                  <span className="contact-field__value">{conversation.peerAddress}</span>
                </div>

                <label className="stack__field">
                  <span>{t('crm.field.job_title')}</span>
                  <input
                    className="input"
                    value={form.jobTitle}
                    maxLength={120}
                    onChange={(event) => set('jobTitle', event.target.value)}
                  />
                </label>

                <label className="stack__field">
                  <span>{t('crm.field.person_type')}</span>
                  <select
                    className="select"
                    value={form.personType}
                    onChange={(event) => set('personType', event.target.value)}
                  >
                    <option value="">—</option>
                    <option value="INDIVIDUAL">{t('crm.personType.INDIVIDUAL')}</option>
                    <option value="COMPANY">{t('crm.personType.COMPANY')}</option>
                  </select>
                </label>

                <label className="stack__field">
                  <span>{t('crm.field.document')}</span>
                  <input
                    className="input"
                    value={form.document}
                    maxLength={32}
                    onChange={(event) => set('document', event.target.value)}
                  />
                </label>

                <label className="stack__field">
                  <span>{t('crm.field.lifecycle_stage')}</span>
                  <select
                    className="select"
                    value={form.lifecycleStage}
                    onChange={(event) => set('lifecycleStage', event.target.value)}
                  >
                    {LIFECYCLE_STAGES.map((stage) => (
                      <option key={stage} value={stage}>
                        {t(`crm.stage.${stage}`)}
                      </option>
                    ))}
                  </select>
                  {/*
                   * "Lead" means two different records in this product, and the owner hit it
                   * within a day of the field existing: a contact moved to the Lead stage and
                   * then went looking for it under CRM › Leads, which lists a different table —
                   * the ones captured and not yet turned into a person. Said here, where the
                   * choice is made, rather than left to be discovered.
                   */}
                  <span className="muted small">{t('crm.field.lifecycle_stage.hint')}</span>
                </label>

                {problem && (
                  <p className="alert alert--compact" role="alert">
                    {t(`inbox.contactProblem.${problem}`)}
                  </p>
                )}

                <div className="cell-inline">
                  <button
                    type="submit"
                    className="button button--primary button--compact"
                    disabled={save.isPending || problem !== null}
                  >
                    {save.isPending ? t('common.saving') : t('common.save')}
                  </button>

                  <button
                    type="button"
                    className="button button--quiet button--compact"
                    onClick={() => (dirty ? setConfirmingDiscard(true) : stopEditing())}
                  >
                    {t('confirm.cancel')}
                  </button>
                </div>
              </form>
            ) : (
              <div className="contact-fields">
                <ReadField label={t('crm.field.email')} value={asText(record.data?.email)} />
                <ReadField label={t('crm.field.phone')} value={asText(record.data?.phone)} />
                <ReadField label={t('inbox.channelNumber')} value={conversation.peerAddress} />
                <ReadField
                  label={t('crm.field.job_title')}
                  value={asText(record.data?.job_title)}
                />
                <ReadField label={t('crm.field.document')} value={asText(record.data?.document)} />
                <ReadField
                  label={t('crm.field.lifecycle_stage')}
                  value={
                    record.data?.lifecycle_stage
                      ? t(`crm.stage.${String(record.data.lifecycle_stage)}`)
                      : null
                  }
                />
              </div>
            )}
          </section>
        )}

        {/* 3. Everything that is not a column: tags and what somebody wrote down. */}
        <section className="drawer-panel__block">
          <h4>{t('inbox.extraInfo')}</h4>

          <div className="chips chips--loose">
            {conversation.tags.map((tag) => {
              // Removable only while editing, and only when the id is known: the conversation
              // carries tag *names*, and detaching needs the row they came from.
              const known = (tags.data?.tags ?? []).find((each) => each.name === tag);

              return (
                <span key={tag} className="chip chip--accent">
                  {tag}
                  {editing && known && (
                    <button
                      type="button"
                      className="chip__remove"
                      aria-label={t('inbox.removeTag').replace('{tag}', tag)}
                      disabled={untagIt.isPending}
                      onClick={() => untagIt.mutate(known.id)}
                    >
                      ×
                    </button>
                  )}
                </span>
              );
            })}
            {conversation.tags.length === 0 && (
              <span className="muted small">{t('inbox.noTags')}</span>
            )}
          </div>

          {contactId && editing && (
            <>
              <div className="cell-inline">
                <input
                  className="input"
                  value={newTag}
                  maxLength={60}
                  placeholder={t('inbox.addTag')}
                  onChange={(event) => setNewTag(event.target.value)}
                />
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  disabled={newTag.trim() === '' || tagIt.isPending}
                  onClick={() => tagIt.mutate(newTag.trim())}
                >
                  +
                </button>
              </div>

              {tagOptions.length > 0 && (
                <div className="chips">
                  {tagOptions.slice(0, 8).map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      className="chip"
                      onClick={() => tagIt.mutate(tag.name)}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {contactId && (
            <div className="stack__field">
              <span>{t('inbox.contactNote')}</span>
              <textarea
                className="input"
                rows={2}
                maxLength={2000}
                value={note}
                placeholder={t('inbox.contactNotePlaceholder')}
                onChange={(event) => setNote(event.target.value)}
              />
              <button
                type="button"
                className="button button--quiet button--compact"
                disabled={note.trim() === '' || addTheNote.isPending}
                onClick={() => addTheNote.mutate()}
              >
                {addTheNote.isPending ? t('common.saving') : t('inbox.addContactNote')}
              </button>
            </div>
          )}
        </section>

        {/* 4. The conversation itself: who is answering, on what, how urgent. */}
        <section className="drawer-panel__block">
          <h4>{t('inbox.serviceBlock')}</h4>

          <div className="contact-fields">
            <AssignmentControls
              conversationId={conversation.id}
              assigneeId={conversation.assigneeId}
              queueId={conversation.queueId}
            />

            <div className="contact-field">
              <span className="contact-field__label">{t('channels.kind')}</span>
              <span className="contact-field__value cell-inline">
                {channelKind && <ChannelMark kind={channelKind} />}
                {channelKind ? t(`channels.kind.${channelKind}`) : '—'}
              </span>
            </div>

            <div className="contact-field">
              <span className="contact-field__label">{t('inbox.priority')}</span>
              <span className="contact-field__value">
                <Badge tone="neutral">{conversation.priority}</Badge>
              </span>
            </div>
          </div>
        </section>

        {/* 5. History, last, as asked: it is the block people scroll to, not the one they land on. */}
        {conversation.events.length > 0 && (
          <section className="drawer-panel__block">
            <h4>{t('inbox.history')}</h4>
            <ol className="history">
              {conversation.events.slice(-12).map((event, index) => (
                <li key={`${event.kind}-${event.at}-${index}`}>
                  <span className="history__dot" aria-hidden="true" />
                  <span className="history__text">{t(`inbox.event.${event.kind}`)}</span>
                  <span className="muted small">
                    {new Date(event.at).toLocaleTimeString('pt-BR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>

      {/*
       * Unsaved work is never thrown away silently.
       *
       * Asked rather than blocked: somebody who wants to abandon a half-typed correction should
       * be able to, in one click, without arguing with the product.
       */}
      {confirmingDiscard && (
        <div className="drawer-panel__confirm" role="alertdialog" aria-live="assertive">
          <p className="strong">{t('inbox.discardTitle')}</p>
          <p className="muted small">{t('inbox.discardBody')}</p>

          <div className="cell-inline">
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setConfirmingDiscard(false)}
            >
              {t('inbox.keepEditing')}
            </button>

            <button
              type="button"
              className="button button--danger button--compact"
              onClick={() => {
                stopEditing();
                onClose();
              }}
            >
              {t('inbox.discard')}
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}

function asText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** A field with no value says so rather than showing a plausible blank. */
function ReadField({ label, value }: { label: string; value: string | null }): React.JSX.Element {
  return (
    <div className="contact-field">
      <span className="contact-field__label">{label}</span>
      <span className="contact-field__value">{value ?? <span className="muted">—</span>}</span>
    </div>
  );
}
